import React, { useEffect, useMemo, useState } from 'react';
import { RegistryLineItemInput, useCreateRegistryPurchaseContextMutation } from '@graphql/generated';
import { withWindow } from '@shared/utils/withWindow';
import { useRoutePaths } from '@apps/registry/guest/GuestRegistry.routes';

import { useShoppingCart } from '@apps/registry/guest/routes/GuestRegistry/components/ShoppingCart/state';
import { CartCheckoutSteps, CartSections, LineItem, ShippingAddressOptions } from '../../../ShoppingCart.types';
import { formatAddress } from '../../../state/ShoppingCart.utils';
import { StyledLoader, StyledLoadingWrapper, styles } from './PaymentStep.styles';
import { useTheme } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { PaymentErrorScreen } from './PaymentErrorScreen';

const formatList = (itemList: Array<LineItem>): Array<RegistryLineItemInput> => itemList.map(item => ({ quantity: item.quantity, registryItemId: item.registryItemId }));

type PaymentStepProps = {
  eventId: string;
};

export const PaymentStep = ({ eventId }: PaymentStepProps) => {
  const {
    state: { itemList, shippingAddress, details, hadOnlyStillNeededItems, selectedShippingOption, verificationStatus },
    mutators: { updateCurrentCheckoutStep, updateCurrentSection, setVerificationStatus }
  } = useShoppingCart();
  const routes = useRoutePaths();
  const { colors } = useTheme();
  const [createPurchaseContext] = useCreateRegistryPurchaseContextMutation();
  const hasOutOfStockError = useMemo(() => itemList.some(item => !item.inStock), [itemList]);
  const hasNotNeededError = !hadOnlyStillNeededItems;
  const [hasCheckoutError, setHasCheckoutError] = useState(false);
  const { t2 } = useTranslation('guestRegistry');
  const { outOfStockErrorText, outOfStockErrorAction, outOfStockErrorTitle, notNeededErrorText, checkoutErrorTitle, checkoutErrorText, checkoutErrorAction } = t2(
    'shoppingCart',
    'paymentStep'
  );

  useEffect(() => {
    setVerificationStatus('started');
  }, [setVerificationStatus]);

  useEffect(() => {
    if (!shippingAddress || !details || !details.email) {
      return;
    }

    if (verificationStatus !== 'done') {
      return;
    }

    if (hasNotNeededError || hasOutOfStockError || hasCheckoutError) {
      return;
    }

    (async () => {
      setVerificationStatus('idle');
      try {
        const failureUrlTemplate = withWindow(global => global.location.origin + routes.guestRegistry.path, '');
        const successUrlTemplate = `${withWindow(global => global.location.origin + routes.purchase.path, '')}?pcId=\${purchaseContextId}`;
        const purchaseContext = await createPurchaseContext({
          variables: {
            payload: {
              eventId: eventId!,
              name: details.name,
              email: details.email,
              note: details.message,
              lineItems: formatList(itemList),
              failureUrlTemplate,
              successUrlTemplate,
              address: selectedShippingOption === ShippingAddressOptions.CoupleAddress ? undefined : formatAddress(shippingAddress),
              eCard: details?.eCard ?? undefined
            }
          }
        });
        const checkoutUrl = purchaseContext?.data?.createRegistryPurchaseContext.checkoutUrl;

        if (checkoutUrl) {
          withWindow(global => (global.location.href = checkoutUrl!));
        } else {
          setHasCheckoutError(true);
        }
      } catch (err) {
        setHasCheckoutError(true);
      }
    })();
  }, [
    itemList,
    shippingAddress,
    details,
    createPurchaseContext,
    eventId,
    routes,
    hasCheckoutError,
    hasNotNeededError,
    hasOutOfStockError,
    selectedShippingOption,
    setVerificationStatus,
    verificationStatus
  ]);

  if (hasOutOfStockError) {
    return (
      <PaymentErrorScreen
        title={outOfStockErrorTitle}
        text={outOfStockErrorText}
        action={outOfStockErrorAction}
        handleAction={() => {
          updateCurrentCheckoutStep(CartCheckoutSteps.Cart);
          updateCurrentSection(CartSections.CART);
        }}
      />
    );
  }
  if (hasNotNeededError) {
    return <>{notNeededErrorText}</>;
  }
  if (hasCheckoutError) {
    return (
      <PaymentErrorScreen
        title={checkoutErrorTitle}
        text={checkoutErrorText}
        action={checkoutErrorAction}
        handleAction={() => {
          updateCurrentCheckoutStep(CartCheckoutSteps.Cart);
        }}
      />
    );
  }

  return (
    <StyledLoadingWrapper __css={styles.loadingWrapperStyles}>
      <svg width={52} height={52} x="0px" y="0px" viewBox="0 0 52 52">
        <circle strokeWidth={4} strokeLinecap="round" r={18} cx={26} cy={26} stroke={colors.mono4} fill="transparent" />
        <StyledLoader strokeWidth={4} strokeLinecap="round" r={18} cx={26} cy={26} />
      </svg>
    </StyledLoadingWrapper>
  );
};
